import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useHasEnoughTimeElapsedSinceEventCreation } from '@apps/admin/routes/Dashboard/hooks/useHasEnoughTimeElapsedSinceEventCreation';
import { useIsAutoPopulationDone } from './useIsAutoPopulationDone';
import { useIsAccommodationsAiRecommendationsEnabled } from '../../useIsAccommodationsAiRecommendationsEnabled';

interface UseAutoPopulationFeatureFlagsReturn
  extends Readonly<{
    asyncHotelAutopopulationEnabled: boolean;
    isAccommodationsAiRecommendationsEnabled: boolean;
    hasEnoughTimeElapsed: boolean;
    isAutoPopulationDoneFromStorage: boolean;
    setIsAutoPopulationDone: () => void;
    isLoadingFeatureFlags: boolean;
  }> {}

/**
 * Extracts and consolidates all feature flag logic for auto-population.
 */
export const useAutoPopulationFeatureFlags = (validateIsEnoughTimeElapsed?: boolean): UseAutoPopulationFeatureFlagsReturn => {
  const [, loadingDialog, , dialogPayload] = useIsFeatureEnabled('showAutoPopulationCompletedDialog');

  const durationHours = (dialogPayload as { durationHours?: number })?.durationHours ?? 0;
  const [hasEnoughTimeElapsed, loadingTimeCheck] = useHasEnoughTimeElapsedSinceEventCreation(durationHours * 60 * 60 * 1000);

  // Feature flag to control whether the async hotel autopopulation is enabled
  const [asyncHotelAutopopulationEnabled, loadingAsyncHotelAutopulationEnabled] = useIsFeatureEnabled('asyncHotelAutopopulationEnabled');

  // Feature flag to control whether the accommodations AI recommendations are enabled
  const { isAccommodationsAiRecommendationsEnabled, isLoading: loadingIsAccommodationsAiRecommendationsEnabled } = useIsAccommodationsAiRecommendationsEnabled();

  const { isAutoPopulationDone: isAutoPopulationDoneFromStorage, setIsAutoPopulationDone, isLoadingAutoPopulationFlag } = useIsAutoPopulationDone();

  const isLoadingFeatureFlags =
    loadingDialog || loadingTimeCheck || loadingAsyncHotelAutopulationEnabled || loadingIsAccommodationsAiRecommendationsEnabled || isLoadingAutoPopulationFlag;
  return {
    asyncHotelAutopopulationEnabled,
    isAccommodationsAiRecommendationsEnabled,
    hasEnoughTimeElapsed: validateIsEnoughTimeElapsed ? hasEnoughTimeElapsed : true,
    isAutoPopulationDoneFromStorage,
    setIsAutoPopulationDone,
    isLoadingFeatureFlags,
  };
};
